import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { 
    getConnectionStats, 
    getSentConnections, 
    getReceivedConnections, 
    respondToConnection,
    getCompanyList,
    getCompanyById,
    getCompanyRecommendations,
    createConnection,
    getActiveConnections,
    cancelConnection,
    getMyCompany
} from '../utils/apiServices'; 
import Popup from './Popup'; 
import '../stylesheets/ConnectionsDashboard.css'; 

// Opciones para el sistema de negocio (para los filtros)
const businessSystemOptions = [
    { value: '', label: 'Todos los Sistemas' },
    { value: 'SALUD_BELLEZA', label: 'Salud/Belleza' },
    { value: 'HOGAR', label: 'Hogar' },
    { value: 'ALIMENTOS', label: 'Alimentos' },
    { value: 'INDUSTRIAS_CREATIVAS', label: 'Industrias creativas' },
    { value: 'MODA', label: 'Moda' },
    { value: 'SERVICIOS', label: 'Servicios' },
    { value: 'OTRO', label: 'Otros' },
];

const allianceTypeOptions = [
    { value: 'PROVEEDURIA', label: 'Proveeduría' },
    { value: 'SERVICIOS', label: 'Servicios' },
    { value: 'COMPRAS_VENTAS_CONJUNTAS', label: 'Compras/Ventas conjuntas' },
    { value: 'COMERCIALIZACION', label: 'Comercialización' },
    { value: 'OTRAS', label: 'Otras' },
];

const allianceTypeLabels = allianceTypeOptions.reduce((acc, option) => {
    acc[option.value] = option.label;
    return acc;
}, {});

const productsServicesOptions = [
    { value: 'ALIMENTOS_PREPARADOS', label: 'Alimentos preparados' },
    { value: 'SERVICIO_RESTAURANTE', label: 'Restaurante / Comidas rápidas' },
    { value: 'MANUFACTURA_ROPA', label: 'Manufactura - Prendas de vestir' },
    { value: 'COMERCIALIZACION_ROPA', label: 'Comercialización de ropa/calzado' },
    { value: 'ACCESORIOS_BISUTERIA', label: 'Accesorios / Bisutería / Joyería' },
    { value: 'PRODUCTOS_BELLEZA', label: 'Productos y servicios de belleza' },
    { value: 'ARTESANIAS', label: 'Artesanías' },
    { value: 'PRODUCTOS_HOGAR', label: 'Productos para el hogar' },
    { value: 'SERVICIOS_PUBLICIDAD', label: 'Servicios de publicidad y diseño' },
    { value: 'SERVICIOS_DECORACION', label: 'Servicios y productos de decoración' },
    { value: 'SERVICIOS_TECNOLOGICOS', label: 'Servicios tecnológicos' },
    { value: 'SERVICIOS_CONTABLES', label: 'Servicios contables' },
    { value: 'OTROS', label: 'Otros' },
];

const productServiceLabels = productsServicesOptions.reduce((acc, option) => {
    acc[option.value] = option.label;
    return acc;
}, {});

const getBusinessSystemLabel = (value) => {
    if (!value) return 'Sin categoría';
    const option = businessSystemOptions.find(opt => opt.value === value);
    return option ? option.label : value;
};

const ConnectionsDashboard = () => {
    const { user } = useContext(AuthContext);
    
    // --- Estados de carga ---
    const [loadingStats, setLoadingStats] = useState(true);
    const [loadingRequests, setLoadingRequests] = useState(true);
    const [loadingCompanies, setLoadingCompanies] = useState(true);
    const [loadingActiveConnections, setLoadingActiveConnections] = useState(true);
    // Lo activa el 400 de /connections/stats ("Debes tener una empresa
    // registrada."). El personal administrativo recibe 200, así que no le aplica.
    const [hasCompanyError, setHasCompanyError] = useState(false);
    const [myCompanyId, setMyCompanyId] = useState(null); 
    const searchInputRef = useRef(null);
    
    // --- Estados para las pestañas ---
    const [activeTab, setActiveTab] = useState('recommendations'); // 'recommendations', 'received', 'sent', 'active', 'search'
    
    // --- Estados para "Mi Actividad" ---
    const [stats, setStats] = useState({ 
        received: { pending: 0, accepted: 0, rejected: 0 },
        sent: { pending: 0, accepted: 0, rejected: 0 },
        total_connections: 0
    });
    const [sentRequests, setSentRequests] = useState([]);
    const [receivedRequests, setReceivedRequests] = useState([]);
    const [activeConnections, setActiveConnections] = useState([]);
    
    // --- Estados para "Recomendaciones" ---
    const [recommendations, setRecommendations] = useState([]);
    const [loadingRecommendations, setLoadingRecommendations] = useState(true);
    const [recommendationsMessage, setRecommendationsMessage] = useState('');

    // --- Estados para "Listado de Empresas" ---
    const [companies, setCompanies] = useState([]); 
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState(""); 
    const [activeFilter, setActiveFilter] = useState(""); // Filtro de sistema de negocio
    
    // --- Estado para el Modal de Conexión ---
    const [connectModal, setConnectModal] = useState({
        isOpen: false,
        selectedCompany: null,
        message: '',
        isSending: false,
        error: ''
    });

    // --- Estado para el Modal de Información de Empresa ---
    const [companyInfoModal, setCompanyInfoModal] = useState({
        isOpen: false,
        company: null,
        loading: false
    });

    // Al abrir la búsqueda, el cursor entra directo en el campo: pulsar la lupa
    // y tener que hacer un segundo clic para escribir es fricción innecesaria.
    useEffect(() => {
        if (activeTab === 'search') {
            searchInputRef.current?.focus();
        }
    }, [activeTab]);

    // --- Cargar información de mi empresa ---
    useEffect(() => {
        const loadMyCompany = async () => {
            if (!user?.token) return;
            
            try {
                const myCompany = await getMyCompany(user.token);
                setMyCompanyId(myCompany.id);
            } catch (error) {
                // Sin empresa el backend responde 404; el panel se renderiza igual
                // y cada sección resuelve su propio estado vacío.
            }
        };
        
        loadMyCompany();
    }, [user]);

    // --- Cargar "Mi Actividad" (Stats, Enviadas, Recibidas, Activas) ---
    // (Esta función se ejecuta solo una vez al cargar)
    useEffect(() => {
        if (!user?.token) return;

        const loadMyActivity = async () => {
            setLoadingStats(true);
            setLoadingRequests(true);
            setLoadingActiveConnections(true);
            try {
                const [statsData, sentData, receivedData, activeData] = await Promise.all([
                    getConnectionStats(user.token),
                    getSentConnections(user.token),
                    getReceivedConnections(user.token),
                    getActiveConnections(user.token)
                ]);
                
                setStats(statsData || { 
                    received: { pending: 0, accepted: 0, rejected: 0 },
                    sent: { pending: 0, accepted: 0, rejected: 0 },
                    total_connections: 0
                });
                
                // Asegurar que los datos sean arrays
                // El backend puede devolver un array directo o un objeto paginado con 'results'
                const processedSent = Array.isArray(sentData) 
                    ? sentData 
                    : (sentData?.results || []);
                    
                const processedReceived = Array.isArray(receivedData) 
                    ? receivedData 
                    : (receivedData?.results || []);
                    
                const processedActive = Array.isArray(activeData) 
                    ? activeData 
                    : (activeData?.results || []);
                
                setSentRequests(processedSent);
                setReceivedRequests(processedReceived);
                setActiveConnections(processedActive);
            } catch (error) {
                console.error("Error cargando actividad:", error);
                
                // Verificar si el error es porque no tiene empresa
                if (error.response?.data?.detail?.includes('empresa registrada')) {
                    setHasCompanyError(true);
                }
                
                // Asegurar que siempre haya datos válidos incluso en caso de error
                setStats({ 
                    received: { pending: 0, accepted: 0, rejected: 0 },
                    sent: { pending: 0, accepted: 0, rejected: 0 },
                    total_connections: 0
                });
                setSentRequests([]);
                setReceivedRequests([]);
                setActiveConnections([]);
            } finally {
                setLoadingStats(false);
                setLoadingRequests(false);
                setLoadingActiveConnections(false);
            }
        };

        loadMyActivity();
    }, [user]);

    // --- Cargar "Recomendaciones" ---
    useEffect(() => {
        const loadRecommendations = async () => {
            if (!user?.token) return;
            
            setLoadingRecommendations(true);
            try {
                const data = await getCompanyRecommendations(user.token);
                setRecommendations(data.recommendations || []);
                if (data.message) {
                    setRecommendationsMessage(data.message);
                }
            } catch (error) {
                console.error("Error cargando recomendaciones:", error);
                setRecommendations([]);
                if (error.response?.data?.detail) {
                    setRecommendationsMessage(error.response.data.detail);
                }
            } finally {
                setLoadingRecommendations(false);
            }
        };

        loadRecommendations();
    }, [user]);

    // --- Cargar "Listado de Empresas" ---
    // (Esta función se ejecutará cada vez que cambie la página, el filtro o la búsqueda)
    const fetchCompanies = useCallback(async () => {
        if (!user?.token) return;
        setLoadingCompanies(true);
        
        const params = {
            page: currentPage,
            company_name: searchTerm,
            company_system: activeFilter,
        };

        try {
            const data = await getCompanyList(user.token, params);
            
            // Django REST Framework devuelve la estructura: { count, next, previous, results }
            if (data.results) {
                // Respuesta paginada de DRF
                setCompanies(data.results);
                
                // Calcular total de páginas: count / PAGE_SIZE (el backend usa PAGE_SIZE=8)
                const pageSize = 8;
                const calculatedTotalPages = Math.ceil(data.count / pageSize);
                setTotalPages(calculatedTotalPages);
            } else if (Array.isArray(data)) {
                // Si por alguna razón devuelve un array directo
                setCompanies(data);
                setTotalPages(1);
            } else {
                // Fallback
                setCompanies([]);
                setTotalPages(1);
            }
        } catch (error) {
            console.error("Error cargando empresas:", error);
            setCompanies([]);
            setTotalPages(1);
        } finally {
            setLoadingCompanies(false);
        }
    }, [user, currentPage, searchTerm, activeFilter]); // Dependencias

    // Efecto que llama a fetchCompanies cuando sus dependencias cambian
    useEffect(() => {
        fetchCompanies();
    }, [fetchCompanies]); // fetchCompanies ya tiene todas las dependencias

    // --- Handlers "Mi Actividad" ---
    const handleRespond = async (requestId, action) => {
        setReceivedRequests(prev => 
            prev.map(req => req.id === requestId ? { ...req, isResponding: true } : req)
        );
        try {
            await respondToConnection(user.token, requestId, action);
            
            // Eliminar la solicitud de la lista
            setReceivedRequests(prev => prev.filter(req => req.id !== requestId));
            
            // Si se aceptó, recargar también las conexiones activas
            if (action === 'accept') {
                const activeData = await getActiveConnections(user.token);
                setActiveConnections(Array.isArray(activeData) ? activeData : (activeData?.results || []));
            }
            
            // Recargar estadísticas para tener datos actualizados
            const statsData = await getConnectionStats(user.token);
            setStats(statsData || stats);
            
        } catch (error) {
            console.error("Error respondiendo a solicitud:", error);
            const errorMsg = error.response?.data?.detail || `Error al ${action === 'accept' ? 'aceptar' : 'rechazar'} la solicitud.`;
            alert(errorMsg);
            
            setReceivedRequests(prev => 
                prev.map(req => req.id === requestId ? { ...req, isResponding: false } : req)
            );
        }
    };

    const handleCancelRequest = async (requestId) => {
        setSentRequests(prev => 
            prev.map(req => req.id === requestId ? { ...req, isCanceling: true } : req)
        );
        try {
            await cancelConnection(user.token, requestId);
            
            // Eliminar la solicitud de la lista
            setSentRequests(prev => prev.filter(req => req.id !== requestId));
            
            // Recargar estadísticas para tener datos actualizados
            const statsData = await getConnectionStats(user.token);
            setStats(statsData || stats);
            
        } catch (error) {
            console.error("Error cancelando solicitud:", error);
            const errorMsg = error.response?.data?.detail || 'Error al cancelar la solicitud.';
            alert(errorMsg);
            
            setSentRequests(prev => 
                prev.map(req => req.id === requestId ? { ...req, isCanceling: false } : req)
            );
        }
    };

    // --- Handlers "Listado de Empresas" ---
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1); // Resetear a página 1 con cada nueva búsqueda
    };

    const handleFilterChange = (e) => {
        setActiveFilter(e.target.value);
        setCurrentPage(1); // Resetear a página 1 con cada nuevo filtro
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };
    
    // --- Handlers "Modal de Conexión" ---
    const handleOpenConnectModal = (company) => {
        setConnectModal({
            isOpen: true,
            selectedCompany: company,
            message: '',
            isSending: false,
            error: ''
        });
    };

    const handleCloseConnectModal = () => {
        if (connectModal.isSending) return; // No cerrar si está enviando
        setConnectModal({ isOpen: false, selectedCompany: null, message: '', isSending: false, error: '' });
    };

    const handleSendConnection = async () => {
        setConnectModal(prev => ({ ...prev, isSending: true, error: '' }));
        try {
            await createConnection(user.token, connectModal.selectedCompany.id, connectModal.message);
            
            // Actualizar "solicitudes enviadas" y estadísticas en tiempo real
            const [statsData, sentData] = await Promise.all([
                getConnectionStats(user.token),
                getSentConnections(user.token)
            ]);
            
            setStats(statsData || stats);
            setSentRequests(Array.isArray(sentData) ? sentData : (sentData?.results || []));
            
            handleCloseConnectModal();
        } catch (error) {
            console.error("Error enviando conexión:", error);
            const errorMsg = error.response?.data?.detail || 'No se pudo enviar la solicitud.';
            setConnectModal(prev => ({ ...prev, isSending: false, error: errorMsg }));
        }
    };

    // --- Handlers "Modal de Información de Empresa" ---
    const openCompanyDetails = async (companyId, baseData = {}) => {
        if (!user?.token) return;

        setCompanyInfoModal({
            isOpen: true,
            company: {
                id: companyId,
                ...baseData
            },
            loading: true
        });

        try {
            const companyDetails = await getCompanyById(user.token, companyId);

            setCompanyInfoModal({
                isOpen: true,
                company: {
                    id: companyId,
                    ...baseData,
                    name: companyDetails.company_name || baseData.name,
                    email: companyDetails.company_email,
                    phone: companyDetails.company_phone,
                    address: companyDetails.company_address,
                    department: companyDetails.main_office_department,
                    municipality: companyDetails.main_office_municipality,
                    description: companyDetails.company_description,
                    system: companyDetails.company_system,
                    nit: companyDetails.company_nit,
                    what_i_seek: companyDetails.what_i_seek,
                    what_i_offer: companyDetails.what_i_offer,
                    allianceTypes: companyDetails.alliance_types || [],
                    allianceTypesOther: companyDetails.alliance_types_other || '',
                    productsServicesOffered: companyDetails.products_services_offered || [],
                    productsServicesOfferedOther: companyDetails.products_services_offered_other || '',
                    productsServicesSeeking: companyDetails.products_services_seeking || [],
                    productsServicesSeekingOther: companyDetails.products_services_seeking_other || '',
                    website: companyDetails.company_website,
                },
                loading: false
            });
        } catch (error) {
            setCompanyInfoModal(prev => ({
                ...prev,
                loading: false
            }));
        }
    };

    const handleOpenCompanyInfo = (connection) => {
        const isFromCompany = connection.from_company !== myCompanyId;
        const companyId = isFromCompany ? connection.from_company : connection.to_company;
        const companyName = isFromCompany ? connection.from_company_name : connection.to_company_name;
        const companySystem = isFromCompany ? connection.from_company_system : connection.to_company_system;

        openCompanyDetails(companyId, {
            name: companyName,
            message: connection.message,
            // Fechas separadas: `responded_at` es cuándo se respondió, no cuándo
            // se envió. Mezclarlas hacía que una solicitud rechazada mostrara la
            // fecha del rechazo bajo la etiqueta "Solicitud enviada".
            sentAt: connection.created_at,
            connectedSince: connection.responded_at,
            status: connection.status,
            statusDisplay: connection.status_display,
            system: companySystem,
            fromRecommendation: false
        });
    };

    const handleOpenRecommendationInfo = (company) => {
        openCompanyDetails(company.id, {
            name: company.company_name,
            system: company.company_system || company.company_system_display,
            fromRecommendation: true
        });
    };

    const handleCloseCompanyInfo = () => {
        setCompanyInfoModal({ isOpen: false, company: null, loading: false });
    };

    // El personal administrativo recorre la plataforma sin empresa propia, para
    // no aparecer en las búsquedas ni en las recomendaciones del resto. Sin
    // empresa no puede emitir ni responder solicitudes, así que se le ofrece una
    // vista de solo lectura en lugar de botones que el backend rechazaría.
    const isAdminObserver = Boolean(user?.is_staff) && !myCompanyId;

    // --- Helper para verificar si ya estoy conectado con una empresa ---
    const isConnected = (companyId) => {
        return activeConnections.some(conn => 
            conn.from_company === companyId || conn.to_company === companyId
        );
    };

    // --- RENDER ---

    // Usuario sin empresa registrada: el panel no tiene nada que mostrarle.
    if (hasCompanyError) {
        return (
            <div className="dashboard-container">
                <h1>Dashboard de Conexiones</h1>
                <div className="error-message">
                    <h2>⚠️ Empresa No Registrada</h2>
                    <p>Para acceder al Dashboard de Conexiones, primero debes registrar tu empresa.</p>
                    <p>Por favor, ve a <a href="/empresa">Perfil de Empresa</a> para registrar tu empresa.</p>
                </div>
            </div>
        );
    }

    
    return (
        <div className="dashboard-container">
            <h1>Dashboard de Conexiones</h1>

            {isAdminObserver && (
                <div className="observer-notice" role="status">
                    <strong>Vista de administrador.</strong> Puedes recorrer toda la
                    plataforma en modo consulta. Las acciones sobre solicitudes están
                    desactivadas para no interferir con las conexiones de los usuarios.
                </div>
            )}

            {/* === ESTADÍSTICAS === */}
            {loadingStats ? <p>Cargando estadísticas...</p> : (
                <div className="stats-grid">
                    <div 
                        className={`stat-card clickable ${activeTab === 'active' ? 'is-current' : ''}`}
                        onClick={() => setActiveTab('active')}
                        aria-current={activeTab === 'active' ? 'page' : undefined}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && setActiveTab('active')}
                        aria-label="Ver conexiones activas"
                    >
                        <h3>{stats.total_connections}</h3><p>Conexiones Activas</p>
                    </div>
                    <div 
                        className={`stat-card clickable ${activeTab === 'received' ? 'is-current' : ''}`}
                        onClick={() => setActiveTab('received')}
                        aria-current={activeTab === 'received' ? 'page' : undefined}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && setActiveTab('received')}
                        aria-label="Ver solicitudes recibidas pendientes"
                    >
                        <h3>{stats.received.pending}</h3><p>Recibidas Pendientes</p>
                    </div>
                    <div 
                        className={`stat-card clickable ${activeTab === 'sent' ? 'is-current' : ''}`}
                        onClick={() => setActiveTab('sent')}
                        aria-current={activeTab === 'sent' ? 'page' : undefined}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && setActiveTab('sent')}
                        aria-label="Ver solicitudes enviadas pendientes"
                    >
                        <h3>{stats.sent.pending}</h3><p>Enviadas Pendientes</p>
                    </div>
                </div>
            )}

            {/* === SISTEMA DE PESTAÑAS ===
                Solicitudes recibidas, enviadas y conexiones activas se abren desde
                las tarjetas de estadísticas de arriba, así que sus pestañas se
                retiraron por redundantes. La búsqueda se abre con la lupa del
                encabezado. */}
            <div className="tabs-container">
                <div className="tabs-header">
                    <button
                        className={`tab-button ${activeTab === 'recommendations' ? 'active' : ''}`}
                        onClick={() => setActiveTab('recommendations')}
                        aria-current={activeTab === 'recommendations' ? 'page' : undefined}
                    >
                        ✨ Conexiones para Ti
                    </button>
                    {/* La búsqueda vive en la misma barra que la navegación porque
                        cumple la misma función: cambiar de vista. Aislada junto al
                        título quedaba sin relación con nada. */}
                    <button
                        type="button"
                        className={`tab-button tab-button--search ${activeTab === 'search' ? 'active' : ''}`}
                        onClick={() => setActiveTab('search')}
                        aria-current={activeTab === 'search' ? 'page' : undefined}
                    >
                        <span className="tab-button__icon" aria-hidden="true">🔍</span>
                        <span className="tab-button__label">Buscar empresas</span>
                    </button>
                </div>

                <div className="tabs-content">
                    {/* ========== PESTAÑA: CONEXIONES PARA TI (RECOMENDACIONES) ========== */}
                    {activeTab === 'recommendations' && (
                        <div className="tab-panel">
                            <h2>{isAdminObserver ? '✨ Conexiones Recomendadas' : '✨ Conexiones Recomendadas para Ti'}</h2>
                            <p className="tab-description">
                                {isAdminObserver
                                    ? 'Cada usuario recibe recomendaciones calculadas sobre su propia empresa.'
                                    : 'Empresas con mayor compatibilidad basadas en tus necesidades, lo que ofreces, tipos de alianzas y productos/servicios específicos'}
                            </p>
                            
                            {loadingRecommendations ? (
                                <div className="loading-state">
                                    <div className="spinner"></div>
                                    <p>Analizando empresas compatibles...</p>
                                </div>
                            ) : (
                                <>
                                    {recommendationsMessage && !isAdminObserver && (
                                        <div className="recommendations-notice">
                                            <span className="notice-icon">💡</span>
                                            <p>{recommendationsMessage}</p>
                                        </div>
                                    )}
                                    
                                    {recommendations.length === 0 ? (
                                        <div className="empty-state">
                                            <div className="empty-icon">🔍</div>
                                            <h3>No se encuentran conexiones recomendadas disponibles</h3>
                                            {/* "Completar Perfil" lleva a registrar empresa: para el
                                                personal administrativo sería contradictorio, porque
                                                justamente no debe tener una. */}
                                            {isAdminObserver ? (
                                                <p className="empty-hint">
                                                    Las recomendaciones se calculan sobre la empresa de cada
                                                    usuario.
                                                </p>
                                            ) : (
                                                <button
                                                    className="btn-primary"
                                                    onClick={() => window.location.href = '/empresa'}
                                                >
                                                    Completar Perfil
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        <>
                                            <div className="recommendations-header">
                                                <p className="recommendations-count">
                                                    🎯 Encontramos <strong>{recommendations.length}</strong> empresa{recommendations.length !== 1 ? 's' : ''} altamente compatible{recommendations.length !== 1 ? 's' : ''} para ti
                                                </p>
                                            </div>
                                            
                                            <div className="companies-grid recommendations-grid">
                                                {recommendations.map(company => {
                                                    // Función helper para obtener el icono según la razón
                                                    const getReasonIcon = (reason) => {
                                                        if (reason.toLowerCase().includes('alianzas')) return '🤝';
                                                        if (reason.toLowerCase().includes('ofrecen productos') || 
                                                            reason.toLowerCase().includes('ofrecen lo que buscas')) return '📦';
                                                        if (reason.toLowerCase().includes('buscan productos') || 
                                                            reason.toLowerCase().includes('buscan lo que ofreces')) return '🛒';
                                                        if (reason.toLowerCase().includes('mismo sector')) return '🏢';
                                                        if (reason.toLowerCase().includes('ubicación')) return '📍';
                                                        if (reason.toLowerCase().includes('sectores complementarios')) return '🔗';
                                                        return '✓';
                                                    };

                                                    // Clasificar el score
                                                    const scoreClass = company.matching_score >= 30 ? 'high' : 
                                                                      company.matching_score >= 15 ? 'medium' : 'low';
                                                    const scoreLabel = company.matching_score >= 30 ? 'Excelente Match' : 
                                                                      company.matching_score >= 15 ? 'Buen Match' : 'Match Potencial';

                                                    return (
                                                        <div key={company.id} className="company-card recommendation-card">
                                                            <div className={`recommendation-badge ${scoreClass}`}>
                                                                <span className="score-label">{scoreLabel}</span>
                                                                <span className="score-value">{company.matching_score} pts</span>
                                                            </div>
                                                            
                                                            <div className="company-header">
                                                                <h3>{company.company_name}</h3>
                                                                <span className="company-category-badge">
                                                                    {company.company_system_display || company.company_system || 'Sin categoría'}
                                                                </span>
                                                            </div>

                                                            {company.company_description && (
                                                                <p className="company-description">
                                                                    {company.company_description.length > 120 ? 
                                                                        company.company_description.substring(0, 120) + '...' : 
                                                                        company.company_description}
                                                                </p>
                                                            )}

                                                            <div className="company-location">
                                                                <span className="location-icon">📍</span>
                                                                <span className="location-text">
                                                                    {company.main_office_municipality && company.main_office_department ? 
                                                                        `${company.main_office_municipality}, ${company.main_office_department}` : 
                                                                        'Ubicación no especificada'}
                                                                </span>
                                                            </div>
                                                            
                                                            <div className="matching-reasons-section">
                                                                <h4 className="reasons-title">✨ ¿Por qué esta empresa?</h4>
                                                                <div className="reasons-list">
                                                                    {company.matching_reasons && company.matching_reasons.length > 0 ? (
                                                                        company.matching_reasons.map((reason, idx) => (
                                                                            <div key={idx} className="reason-item">
                                                                                <span className="reason-icon">{getReasonIcon(reason)}</span>
                                                                                <span className="reason-text">{reason}</span>
                                                                            </div>
                                                                        ))
                                                                    ) : (
                                                                        <div className="reason-item">
                                                                            <span className="reason-icon">✓</span>
                                                                            <span className="reason-text">Empresa compatible con tus necesidades</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            <button 
                                                                className="btn-view-info" 
                                                                onClick={() => handleOpenRecommendationInfo(company)}
                                                            >
                                                                ℹ️ Ver información
                                                            </button>

                                                            <div className="card-actions">
                                                                {isAdminObserver ? null : isConnected(company.id) ? (
                                                                    <button className="btn-connected" disabled>
                                                                        <span className="btn-icon">✓</span>
                                                                        Ya Conectado
                                                                    </button>
                                                                ) : (
                                                                    <button 
                                                                        className="btn-connect-recommendation"
                                                                        onClick={() => handleOpenConnectModal(company)}
                                                                    >
                                                                        <span className="btn-icon">🤝</span>
                                                                        Solicitar Conexión
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {/* ========== PESTAÑA: SOLICITUDES RECIBIDAS ========== */}
                    {activeTab === 'received' && (
                        <div className="tab-panel">
                            <h2>Solicitudes Recibidas</h2>
                            {loadingRequests ? <p>Cargando...</p> : (
                                receivedRequests.length === 0 ? (
                                    <p className="empty-message">No tienes solicitudes recibidas pendientes.</p>
                                ) : (
                                    <ul className="requests-list">
                                        {receivedRequests.map(req => (
                                            <li key={req.id} className="request-card received">
                                                <div className="request-header">
                                                    <h3>{req.from_company_name}</h3>
                                                    <span className="request-date">{new Date(req.created_at).toLocaleDateString()}</span>
                                                </div>
                                                <p className="request-message">"{req.message || 'Sin mensaje.'}"</p>
                                                <div className="request-actions">
                                                    <button 
                                                        className="btn-view-info" 
                                                        onClick={() => handleOpenCompanyInfo(req)}
                                                    >
                                                        ℹ️ Ver información
                                                    </button>
                                                    {!isAdminObserver && (
                                                        <>
                                                            <button
                                                                className="btn-accept"
                                                                onClick={() => handleRespond(req.id, 'accept')}
                                                                disabled={req.isResponding}
                                                            >
                                                                {req.isResponding ? 'Procesando...' : '✓ Aceptar'}
                                                            </button>
                                                            <button
                                                                className="btn-reject"
                                                                onClick={() => handleRespond(req.id, 'reject')}
                                                                disabled={req.isResponding}
                                                            >
                                                                {req.isResponding ? 'Procesando...' : '✗ Rechazar'}
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )
                            )}
                        </div>
                    )}

                    {/* ========== PESTAÑA: SOLICITUDES ENVIADAS ========== */}
                    {activeTab === 'sent' && (
                        <div className="tab-panel">
                            <h2>Solicitudes Enviadas</h2>
                            {loadingRequests ? <p>Cargando...</p> : (
                                sentRequests.length === 0 ? (
                                    <p className="empty-message">No has enviado solicitudes.</p>
                                ) : (
                                    <ul className="requests-list">
                                        {sentRequests.map(req => (
                                            <li key={req.id} className={`request-card sent status-${req.status}`}>
                                                <div className="request-header">
                                                    <h3>{req.to_company_name}</h3>
                                                    <span className={`request-status status-${req.status}`}>
                                                        {req.status_display || req.status}
                                                    </span>
                                                </div>
                                                <p className="request-message">"{req.message || 'Sin mensaje.'}"</p>
                                                <p className="request-date">Enviada: {new Date(req.created_at).toLocaleDateString()}</p>
                                                <div className="request-actions">
                                                    <button 
                                                        className="btn-view-info" 
                                                        onClick={() => handleOpenCompanyInfo(req)}
                                                    >
                                                        ℹ️ Ver información
                                                    </button>
                                                    {!isAdminObserver && req.status === 'pending' && (
                                                        <button
                                                            className="btn-cancel"
                                                            onClick={() => handleCancelRequest(req.id)}
                                                            disabled={req.isCanceling}
                                                        >
                                                            {req.isCanceling ? 'Cancelando...' : 'Cancelar Solicitud'}
                                                        </button>
                                                    )}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )
                            )}
                        </div>
                    )}

                    {/* ========== PESTAÑA: CONEXIONES ACTIVAS ========== */}
                    {activeTab === 'active' && (
                        <div className="tab-panel">
                            <h2>Conexiones Activas</h2>
                            {loadingActiveConnections ? <p>Cargando...</p> : (
                                activeConnections.length === 0 ? (
                                    <p className="empty-message">Aún no tienes conexiones activas. ¡Comienza a conectar con otras empresas!</p>
                                ) : (
                                    <ul className="connections-list">
                                        {activeConnections.map(conn => {
                                            // Determinar cuál es la otra empresa
                                            const otherCompany = conn.from_company !== myCompanyId 
                                                ? conn.from_company_name 
                                                : conn.to_company_name;
                                            
                                            return (
                                                <li 
                                                    key={conn.id} 
                                                    className="connection-card active clickable"
                                                    onClick={() => handleOpenCompanyInfo(conn)}
                                                >
                                                    <div className="connection-header">
                                                        <h3>{otherCompany}</h3>
                                                        <span className="connection-badge">✓ Activa</span>
                                                    </div>
                                                    <p className="connection-message">"{conn.message || 'Sin mensaje.'}"</p>
                                                    <p className="connection-date">
                                                        Conectados desde: {new Date(conn.responded_at || conn.created_at).toLocaleDateString()}
                                                    </p>
                                                    <button className="btn-view-info">Ver información</button>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )
                            )}
                        </div>
                    )}

                    {/* ========== PESTAÑA: BUSCAR EMPRESAS ========== */}
                    {activeTab === 'search' && (
                        <div className="tab-panel">
                            <h2>Buscar Empresas</h2>
                            
                            {/* --- Búsqueda y Filtros --- */}
                            <div className="search-filter-bar">
                                <div className="search-bar form-group">
                                    <label htmlFor="search">Buscar por nombre o descripción</label>
                                    <input
                                        type="text"
                                        id="search"
                                        placeholder="Buscar..."
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                    />
                                </div>
                                <div className="filters form-group">
                                    <label htmlFor="business_system_filter">Filtrar por sistema</label>
                                    <select
                                        id="business_system_filter"
                                        value={activeFilter}
                                        onChange={handleFilterChange}
                                    >
                                        {businessSystemOptions.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* --- Listado de Empresas --- */}
                            {loadingCompanies ? <p>Cargando empresas...</p> : (
                                <>
                                    <div className="companies-grid">
                                        {/* Sin filtros aplicados, "con esos criterios" hace pensar
                                            en una búsqueda fallida cuando en realidad todavía no
                                            hay ninguna empresa registrada. */}
                                        {companies.length === 0 ? (
                                            <p className="empty-message">
                                                {searchTerm || activeFilter
                                                    ? 'No se encontraron empresas con esos criterios.'
                                                    : 'Todavía no hay empresas registradas en la plataforma.'}
                                            </p>
                                        ) : (
                                            companies.map(company => (
                                                <div key={company.id} className="company-card">
                                                    <h3>{company.company_name}</h3>
                                                    <p className="company-card-category">{company.company_system_display || company.company_system || 'Sin categoría'}</p>
                                                    <p className="company-card-description">
                                                        {company.company_description ? 
                                                            (company.company_description.length > 100 ? 
                                                                company.company_description.substring(0, 100) + '...' : 
                                                                company.company_description) 
                                                            : 'Sin descripción'}
                                                    </p>
                                                    <p className="company-card-location">
                                                        {company.main_office_municipality && company.main_office_department ? 
                                                            `${company.main_office_municipality}, ${company.main_office_department}` : 
                                                            'Ubicación no especificada'}
                                                    </p>
                                                    <div className="company-card-actions">
                                                        <button
                                                            className="btn-view-info"
                                                            onClick={() => handleOpenRecommendationInfo(company)}
                                                        >
                                                            ℹ️ Ver información
                                                        </button>
                                                        {isAdminObserver ? null : isConnected(company.id) ? (
                                                            <button className="btn-connected" disabled>
                                                                <span className="btn-icon">✓</span>
                                                                Ya Conectado
                                                            </button>
                                                        ) : (
                                                            <button
                                                                className="btn-connect"
                                                                onClick={() => handleOpenConnectModal(company)}
                                                            >
                                                                Conectar
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>

                                    {/* --- Paginación --- */}
                                    <div className="pagination">
                                        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                                            &laquo; Anterior
                                        </button>
                                        <span>Página {currentPage} de {totalPages}</span>
                                        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
                                            Siguiente &raquo;
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* === MODAL PARA ENVIAR CONEXIÓN === */}
            {connectModal.isOpen && connectModal.selectedCompany && (
                <Popup title={`Conectar con ${connectModal.selectedCompany.company_name}`} onClose={handleCloseConnectModal}>
                    <div className="connect-modal-content">
                        <p>Envía un mensaje junto a tu solicitud de conexión (mínimo 10 caracteres).</p>
                        <div className="form-group">
                            <label htmlFor="connect_message">Mensaje</label>
                            <textarea
                                id="connect_message"
                                rows="4"
                                placeholder="Ej: ¡Hola! Me gustaría conectar con tu empresa para explorar oportunidades de colaboración..."
                                value={connectModal.message}
                                onChange={(e) => setConnectModal(prev => ({ ...prev, message: e.target.value }))}
                            />
                        </div>
                        <button 
                            className="btn-send-connection"
                            onClick={handleSendConnection}
                            disabled={connectModal.isSending || connectModal.message.length < 10}
                        >
                            {connectModal.isSending ? 'Enviando...' : 'Enviar Solicitud'}
                        </button>
                        {connectModal.error && <p className="message error">{connectModal.error}</p>}
                    </div>
                </Popup>
            )}

            {/* === MODAL DE INFORMACIÓN DE EMPRESA === */}
            {companyInfoModal.isOpen && companyInfoModal.company && (
                <Popup title={companyInfoModal.company.name} onClose={handleCloseCompanyInfo}>
                    <div className="company-info-modal-content">
                        {companyInfoModal.loading ? (
                            <div className="loading-info">
                                <p>Cargando información de la empresa...</p>
                            </div>
                        ) : (
                            <>
                                {/* Información General */}
                                <div className="info-section">
                                    <h3>🏢 Información General</h3>
                                    <div className="info-grid">
                                        <div className="info-row">
                                            <span className="info-label">Nombre:</span>
                                            <span className="info-value">{companyInfoModal.company.name}</span>
                                        </div>
                                        {companyInfoModal.company.nit && (
                                            <div className="info-row">
                                                <span className="info-label">NIT:</span>
                                                <span className="info-value">{companyInfoModal.company.nit}</span>
                                            </div>
                                        )}
                                        {companyInfoModal.company.system && (
                                            <div className="info-row">
                                                <span className="info-label">Sector:</span>
                                                <span className="info-value">{getBusinessSystemLabel(companyInfoModal.company.system)}</span>
                                            </div>
                                        )}
                                        {companyInfoModal.company.status && (
                                            <div className="info-row">
                                                <span className="info-label">Estado:</span>
                                                <span className={`info-value status-badge status-${companyInfoModal.company.status}`}>
                                                    {companyInfoModal.company.statusDisplay || companyInfoModal.company.status}
                                                </span>
                                            </div>
                                        )}
                                        {!companyInfoModal.company.fromRecommendation && companyInfoModal.company.status === 'accepted' && companyInfoModal.company.connectedSince && (
                                            <div className="info-row">
                                                <span className="info-label">Conectados desde:</span>
                                                <span className="info-value">
                                                    {new Date(companyInfoModal.company.connectedSince).toLocaleDateString('es-ES', {
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric'
                                                    })}
                                                </span>
                                            </div>
                                        )}
                                        {/* La fecha de envío solo se muestra si la solicitud
                                            existe realmente, y siempre con su propia fecha. */}
                                        {!companyInfoModal.company.fromRecommendation && companyInfoModal.company.sentAt && (
                                            <div className="info-row">
                                                <span className="info-label">Solicitud enviada:</span>
                                                <span className="info-value">
                                                    {new Date(companyInfoModal.company.sentAt).toLocaleDateString('es-ES', {
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric'
                                                    })}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Descripción */}
                                {companyInfoModal.company.description && (
                                    <div className="info-section">
                                        <h3>📝 Descripción</h3>
                                        <div className="description-box">
                                            {companyInfoModal.company.description}
                                        </div>
                                    </div>
                                )}

                                {/* Información de Contacto */}
                                {(companyInfoModal.company.email || companyInfoModal.company.phone) && (
                                    <div className="info-section">
                                        <h3>📞 Información de Contacto</h3>
                                        <div className="info-grid">
                                            {companyInfoModal.company.email && (
                                                <div className="info-row">
                                                    <span className="info-label">Correo:</span>
                                                    <span className="info-value">
                                                        <a href={`mailto:${companyInfoModal.company.email}`} className="contact-link">
                                                            {companyInfoModal.company.email}
                                                        </a>
                                                    </span>
                                                </div>
                                            )}
                                            {companyInfoModal.company.phone && (
                                                <div className="info-row">
                                                    <span className="info-label">Teléfono:</span>
                                                    <span className="info-value">
                                                        <a href={`tel:${companyInfoModal.company.phone}`} className="contact-link">
                                                            {companyInfoModal.company.phone}
                                                        </a>
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Ubicación */}
                                {((companyInfoModal.company.status === 'accepted' && companyInfoModal.company.address) || companyInfoModal.company.municipality) && (
                                    <div className="info-section">
                                        <h3>📍 Ubicación</h3>
                                        <div className="info-grid">
                                            {/* La dirección solo se revela cuando ya existe una
                                                conexión aceptada; en listados públicos y en
                                                recomendaciones queda oculta. */}
                                            {companyInfoModal.company.status === 'accepted' && companyInfoModal.company.address && (
                                                <div className="info-row">
                                                    <span className="info-label">Dirección:</span>
                                                    <span className="info-value">{companyInfoModal.company.address}</span>
                                                </div>
                                            )}
                                            {companyInfoModal.company.municipality && (
                                                <div className="info-row">
                                                    <span className="info-label">Ciudad:</span>
                                                    <span className="info-value">
                                                        {companyInfoModal.company.municipality}
                                                        {companyInfoModal.company.department && `, ${companyInfoModal.company.department}`}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* ¿Qué busca? */}
                                {companyInfoModal.company.what_i_seek && (
                                    <div className="info-section">
                                        <h3>🔍 ¿Qué Busca?</h3>
                                        <div className="description-box" style={{borderLeftColor: '#ffc107'}}>
                                            {companyInfoModal.company.what_i_seek}
                                        </div>
                                    </div>
                                )}

                                {/* ¿Qué ofrece? */}
                                {companyInfoModal.company.what_i_offer && (
                                    <div className="info-section">
                                        <h3>💼 ¿Qué Ofrece?</h3>
                                        <div className="description-box" style={{borderLeftColor: '#17a2b8'}}>
                                            {companyInfoModal.company.what_i_offer}
                                        </div>
                                    </div>
                                )}

                                {/* Tipos de alianzas */}
                                {(companyInfoModal.company.allianceTypes?.length || companyInfoModal.company.allianceTypesOther) && (
                                    <div className="info-section">
                                        <h3>🤝 Tipos de Alianzas</h3>
                                        {companyInfoModal.company.allianceTypes?.length > 0 && (
                                            <div className="pill-list">
                                                {companyInfoModal.company.allianceTypes.map((type) => (
                                                    <span key={type} className="pill-item">
                                                        {allianceTypeLabels[type] || type}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        {companyInfoModal.company.allianceTypesOther && (
                                            <p className="info-note">Otros: {companyInfoModal.company.allianceTypesOther}</p>
                                        )}
                                    </div>
                                )}

                                {/* Productos / servicios que ofrece */}
                                {(companyInfoModal.company.productsServicesOffered?.length || companyInfoModal.company.productsServicesOfferedOther) && (
                                    <div className="info-section">
                                                        <h3>📦 Productos/Servicios que Ofrece</h3>
                                        {companyInfoModal.company.productsServicesOffered?.length > 0 && (
                                            <div className="pill-list">
                                                {companyInfoModal.company.productsServicesOffered.map((item) => (
                                                    <span key={item} className="pill-item">
                                                        {productServiceLabels[item] || item}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        {companyInfoModal.company.productsServicesOfferedOther && (
                                            <p className="info-note">Otros: {companyInfoModal.company.productsServicesOfferedOther}</p>
                                        )}
                                    </div>
                                )}

                                {/* Productos / servicios que busca */}
                                {(companyInfoModal.company.productsServicesSeeking?.length || companyInfoModal.company.productsServicesSeekingOther) && (
                                    <div className="info-section">
                                        <h3>🛒 Productos/Servicios que Busca</h3>
                                        {companyInfoModal.company.productsServicesSeeking?.length > 0 && (
                                            <div className="pill-list">
                                                {companyInfoModal.company.productsServicesSeeking.map((item) => (
                                                    <span key={item} className="pill-item">
                                                        {productServiceLabels[item] || item}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        {companyInfoModal.company.productsServicesSeekingOther && (
                                            <p className="info-note">Otros: {companyInfoModal.company.productsServicesSeekingOther}</p>
                                        )}
                                    </div>
                                )}

                                {/* Mensaje de Conexión */}
                                {companyInfoModal.company.message && (
                                    <div className="info-section">
                                        <h3>💬 Mensaje de Conexión</h3>
                                        <div className="message-box">
                                            "{companyInfoModal.company.message}"
                                        </div>
                                    </div>
                                )}

                                <div className="modal-actions">
                                    <button className="btn-close-modal" onClick={handleCloseCompanyInfo}>
                                        Cerrar
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </Popup>
            )}
        </div>
    );
};

export default ConnectionsDashboard;